import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, RotateCw, ChevronRight } from 'lucide-react';
import { apiGet } from '@/shared/api/client';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';

interface AuditRow {
  id: string;
  ts: string;
  actor_id?: string;
  actor_name?: string;
  action: string;
  entity: string;
  entity_id?: string;
  payload_json?: string;
}

const ACTION_TONE: Record<string, any> = {
  CREATE: 'success', UPDATE: 'info', DELETE: 'danger',
  LOGIN: 'brand', LOGOUT: 'neutral', SCAN: 'info', RELEASE: 'warning',
};

function fmtTs(ts: string) {
  try { return new Date(ts).toLocaleString('pt-BR'); } catch { return ts; }
}

function PayloadView({ json }: { json?: string }) {
  if (!json) return null;
  let parsed: any;
  try { parsed = JSON.parse(json); } catch { return <pre className="text-xs">{json}</pre>; }
  return (
    <pre className="overflow-auto rounded bg-neutral-50 p-2 text-xs dark:bg-neutral-900">
{JSON.stringify(parsed, null, 2)}
    </pre>
  );
}

export function AuditoriaPage() {
  const [action, setAction] = useState('');
  const [entity, setEntity] = useState('');
  const [actor, setActor] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const list = useQuery({
    queryKey: ['audit', action, entity, actor],
    queryFn: () => {
      const p = new URLSearchParams();
      if (action) p.set('action', action);
      if (entity) p.set('entity', entity);
      if (actor) p.set('actor', actor);
      p.set('limit', '200');
      return apiGet<{ items: AuditRow[] }>(`/v1/audit?${p.toString()}`);
    },
  });

  const rows = list.data?.items ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Trilha de Auditoria"
          subtitle="Todos os eventos imutáveis: quem fez o quê, quando e onde (RDC 15 / ISO 17665)"
          action={
            <Button variant="ghost" size="sm" leadingIcon={<RotateCw className="h-4 w-4" />} onClick={() => list.refetch()}>
              Atualizar
            </Button>
          }
        />
        <CardBody>
          <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Input placeholder="Ação (CREATE, UPDATE, LOGIN...)" value={action} onChange={(e) => setAction(e.target.value)} leadingIcon={<Search className="h-4 w-4" />} />
            <Input placeholder="Entidade (item, batch, equipment...)" value={entity} onChange={(e) => setEntity(e.target.value)} leadingIcon={<Search className="h-4 w-4" />} />
            <Input placeholder="ID do ator (usuário)" value={actor} onChange={(e) => setActor(e.target.value)} leadingIcon={<Search className="h-4 w-4" />} />
          </div>

          {list.isLoading ? (
            <p className="py-8 text-center text-sm text-neutral-500">Carregando trilha...</p>
          ) : rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-neutral-500">Nenhum evento.</p>
          ) : (
            <ol className="relative border-l border-neutral-200 dark:border-neutral-700">
              {rows.map((r) => {
                const open = !!expanded[r.id];
                return (
                  <li key={r.id} className="mb-3 ml-4">
                    <span className="absolute -left-1.5 mt-2 h-3 w-3 rounded-full border border-neutral-300 bg-neutral-0 dark:border-neutral-600 dark:bg-neutral-800" />
                    <button
                      type="button"
                      onClick={() => setExpanded((s) => ({ ...s, [r.id]: !open }))}
                      className="flex w-full items-start gap-3 rounded border border-neutral-200 bg-neutral-0 p-3 text-left hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800 dark:hover:bg-neutral-700/30"
                    >
                      <ChevronRight className={`mt-0.5 h-4 w-4 shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-90' : ''}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <Badge tone={ACTION_TONE[r.action] ?? 'neutral'}>{r.action}</Badge>
                          <span className="font-mono text-xs text-neutral-500">{r.entity}</span>
                          {r.entity_id ? <span className="truncate font-mono text-xs text-neutral-400">{r.entity_id}</span> : null}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-3 text-xs text-neutral-500">
                          <span>{fmtTs(r.ts)}</span>
                          {r.actor_name ? <span>por {r.actor_name}</span> : r.actor_id ? <span className="font-mono">por {r.actor_id.slice(0, 8)}</span> : null}
                        </div>
                      </div>
                    </button>
                    {open && r.payload_json ? <div className="mt-2 ml-7"><PayloadView json={r.payload_json} /></div> : null}
                  </li>
                );
              })}
            </ol>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
